import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useTranslation } from './hooks/useTranslation';
import ReactMarkdown from 'react-markdown';
import LimitReachedBanner from './components/LimitReachedBanner';
import UpgradeModal from './components/UpgradeModal';
import PremiumCelebrationModal from './components/PremiumCelebrationModal';

import { 
  LayoutDashboard, 
  History as HistoryIcon, 
  BrainCircuit, 
  UserCircle, 
  Plus, 
  Dog, 
  Cat,
  Weight, 
  Activity, 
  Calendar,
  Send,
  Loader2,
  X,
  ChevronRight,
  LogOut,
  CheckCircle2,
  AlertCircle,
  Bell,
  Stethoscope,
  Trash2,
  Utensils,
  FileText,
  Clock,
  AlertTriangle,
  Camera,
  Mic,
  Palette,
  Database,
  ChevronDown,
  PawPrint,
  Hash,
  Mail,
  Edit2,
  Circle,
  User,
  Menu,
  ChevronLeft,
  Home,
  MessageCircle,
  Settings,
  MicOff,
  Check,
  X as XIcon,
  Loader2 as Loader2Icon
} from 'lucide-react';

import { 
  onAuthStateChanged, 
  sendPasswordResetEmail,
  sendSignInLinkToEmail
} from "firebase/auth";
import type { User as FirebaseUser } from "firebase/auth";
import { 
  collection, 
  query, 
  where, 
  getDocs,
  addDoc,
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  onSnapshot,
  orderBy,
  limit,
  startAfter,
  startAt,
  endAt,
  endBefore
} from "firebase/firestore";
import { getFirebaseDB } from './services/firebase';

import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  ReferenceLine
} from 'recharts';

import { PetProfile, HealthEntry, View, AuthView, Reminder, PetType } from './types';
import { MOODS, APPETITE_LEVELS, REMINDER_TYPES, STOOL_SCORES, STOOL_COLORS } from './constants';
import { COUNTRIES } from '../shared/constants/countries';
import { getHealthAdvice, analyzeVoiceLog } from './services/geminiService';
import { Header, Navigation } from './components';
import { Dashboard, History } from './pages';
import { useModalState } from './hooks/useModalState';
import { PetEditModal } from './modals/PetEditModal';
import { ReminderModal } from './modals/ReminderModal';
import { UserEditModal } from './modals';
import { LogForm } from './forms';
import { auth } from './services/firebase';
import { storage } from './services/storageService';
import { saveUserProfile, getUserProfile, updateUserLastLogin, updateUserRole } from './services/userService';
import { useOAuth } from './hooks/useOAuth';
import { useSubscription } from './hooks/useSubscription';
import { useLogsReminders } from './hooks/useLogsReminders';
import { useFirebaseAuthActions } from './hooks/useFirebaseAuthActions';
import { usePetManagement } from './hooks/usePetManagement';
import { LogDetailModal } from './components/LogDetailModal';
import { WeightChart, AIAdvisor, RemindersList, PetStatsCards } from './components/dashboard';
import { HistoryView, AIChatView, ProfileView } from './components/views';
import LanguageSelector from './components/LanguageSelector';
import { subscriptionService } from './services/subscriptionService';
import { SubscriptionType } from './types/subscription';

const App: React.FC = () => {
  // ===== 1. TOUS LES HOOKS D'ABORD =====
  const { t, currentLanguage } = useTranslation();
  const { session: oauthSession, user: oauthUser, isAuthenticated: isOAuthAuthenticated, loading: oauthLoading, login: oauthLogin, logout: oauthLogout, handleCallback } = useOAuth();
  const { subscription, canPerformAction, updateUsage, isFree, error: subscriptionError } = useSubscription();

  // ===== 2. TOUS LES useState =====
  const [currentUser, setCurrentUser] = useState<FirebaseUser | null>(null);
  const [authView, setAuthView] = useState<AuthView>('login');
  const [appView, setAppView] = useState<View>('dashboard');
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [pets, setPets] = useState<PetProfile[]>([]);
  const [currentPetId, setCurrentPetId] = useState<string | null>(null);

  const [statusMessage, setStatusMessage] = useState<{type: 'success'|'error', text: string} | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [currentPassword, setCurrentPassword] = useState<string>('');
  const modalState = useModalState();
  const [showPWAInstall, setShowPWAInstall] = useState(false);
  const [selectedLog, setSelectedLog] = useState<HealthEntry | null>(null);
  const [newLog, setNewLog] = useState({
    date: new Date().toISOString().split('T')[0],
    time: new Date().toLocaleTimeString('fr-FR', {hour: '2-digit', minute:'2-digit'}),
    weight: '',
    mood: '' as HealthEntry['mood'] | '',
    appetite: '' as HealthEntry['appetite'] | '',
    stoolScore: '' as number | '',
    stoolColor: '' as string | '',
    notes: ''
  });
  const [isListening, setIsListening] = useState(false);
  const [voiceProcessing, setVoiceProcessing] = useState(false);
  const [isChatListening, setIsChatListening] = useState(false);
  const [newReminder, setNewReminder] = useState({
    type: 'veterinaire' as Reminder['type'],
    date: '',
    title: ''
  });
  const [aiLoading, setAiLoading] = useState(false);
  const [aiInput, setAiInput] = useState('');
  const [chatMessages, setChatMessages] = useState<{role: 'user' | 'ai', text: string}[]>([]);
  const [isEditUserModalOpen, setIsEditUserModalOpen] = useState(false);
  const [userProfile, setUserProfile] = useState<any>(null);
  const [userForm, setUserForm] = useState({
    name: '',
    firstName: '',
    email: '',
    address: {
      street: '',
      postalCode: '',
      city: '',
      country: ''
    }
  });

  // États pour les bannières de limite atteinte
  const [showPetLimitBanner, setShowPetLimitBanner] = useState(false);
  const [showLogLimitBanner, setShowLogLimitBanner] = useState(false);
  const [showAiLimitBanner, setShowAiLimitBanner] = useState(false);
  const [showReminderLimitBanner, setShowReminderLimitBanner] = useState(false);
  const [reachedLimits, setReachedLimits] = useState<Array<{
    type: 'pet' | 'log' | 'ai' | 'reminder';
    currentCount: number;
    maxCount: number;
  }>>([]);

  // État pour la modal d'upgrade
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const [isCancellingSubscription, setIsCancellingSubscription] = useState(false);
  const [isPremiumCelebrationOpen, setIsPremiumCelebrationOpen] = useState(false);

  // ===== 3. useRef =====
  const chatEndRef = useRef<HTMLDivElement>(null);

  // ===== 4. FONCTIONS UTILITAIRES =====
  // Vérifier et afficher les bannières de limite
  const checkAndShowLimitBanners = useCallback(async () => {
    if (!subscription) return;
    
    try {
      // Utiliser les données du backend (subscription.usage) plutôt que les états locaux
      const currentPetsCount = subscription.usage.currentPets || 0;
      const currentLogsCount = subscription.usage.currentLogs || 0;
      const currentRemindersCount = subscription.usage.currentReminders || 0;
      const currentAiChatsCount = subscription.usage.currentAiChats || 0;
      
      // Construire la liste des limites atteintes
      const limitsReached: Array<{
        type: 'pet' | 'log' | 'ai' | 'reminder';
        currentCount: number;
        maxCount: number;
      }> = [];
      
      if (currentPetsCount >= subscription.limits.maxPets) {
        limitsReached.push({
          type: 'pet',
          currentCount: currentPetsCount,
          maxCount: subscription.limits.maxPets
        });
      }
      
      if (currentLogsCount >= subscription.limits.maxLogs) {
        limitsReached.push({
          type: 'log',
          currentCount: currentLogsCount,
          maxCount: subscription.limits.maxLogs
        });
      }
      
      if (currentRemindersCount >= subscription.limits.maxReminders) {
        limitsReached.push({
          type: 'reminder',
          currentCount: currentRemindersCount,
          maxCount: subscription.limits.maxReminders
        });
      }
      
      if (currentAiChatsCount >= subscription.limits.maxAiChats) {
        limitsReached.push({
          type: 'ai',
          currentCount: currentAiChatsCount,
          maxCount: subscription.limits.maxAiChats
        });
      }
      
      // Mettre à jour la liste des limites atteintes
      setReachedLimits(limitsReached);
      
      // Garder la compatibilité avec les anciens états (pour les handlers)
      setShowPetLimitBanner(currentPetsCount >= subscription.limits.maxPets);
      setShowLogLimitBanner(currentLogsCount >= subscription.limits.maxLogs);
      setShowReminderLimitBanner(currentRemindersCount >= subscription.limits.maxReminders);
      setShowAiLimitBanner(currentAiChatsCount >= subscription.limits.maxAiChats);
      
    } catch (error) {
      console.error('Error checking limits:', error);
    }
  }, [subscription]);

  const {
    history,
    reminders,
    reloadPetData,
    saveLog: saveLogAndReload,
    deleteLog: deleteLogAndReload,
    saveReminder: saveReminderAndReload,
    deleteReminder: deleteReminderAndReload,
    setHistory,
    setReminders
  } = useLogsReminders({
    updateUsage,
    onLimitsChanged: checkAndShowLimitBanners
  });

  // ===== 5. AUTRES FONCTIONS UTILITAIRES =====
  const validatePassword = (password: string): boolean => {
    if (password.length < 8) return false;
    if (!/[A-Z]/.test(password)) return false;
    if (!/[a-z]/.test(password)) return false;
    if (!/\d/.test(password)) return false;
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) return false;
    return true;
  };

  const getPasswordRules = (password: string) => {
    return {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /\d/.test(password),
      special: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)
    };
  };

  const getPetLogsCount = (petId: string) => {
    return history.filter(log => log.petId === petId).length;
  };

  const formatDateFR = (d: string) => { try { return new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' }); } catch { return ""; } };
  const formatTimeFR = (d: string) => { try { return new Date(d).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }); } catch { return ""; } };

  // Composant pour afficher les règles de mot de passe
  const PasswordRules = ({ password }: { password: string }) => {
    const rules = getPasswordRules(password);
    
    return (
      <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-2xl text-sm">
        <p className="font-semibold mb-2">{t('app:auth.password_requirements')}</p>
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            {rules.length ? (
              <Check className="w-4 h-4 text-green-600" />
            ) : (
              <XIcon className="w-4 h-4 text-red-600" />
            )}
            <span className={rules.length ? 'text-green-700' : 'text-red-700'}>
              {t('app:auth.password_length')}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {rules.uppercase ? (
              <Check className="w-4 h-4 text-green-600" />
            ) : (
              <XIcon className="w-4 h-4 text-red-600" />
            )}
            <span className={rules.uppercase ? 'text-green-700' : 'text-red-700'}>
              {t('app:auth.password_uppercase')}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {rules.lowercase ? (
              <Check className="w-4 h-4 text-green-600" />
            ) : (
              <XIcon className="w-4 h-4 text-red-600" />
            )}
            <span className={rules.lowercase ? 'text-green-700' : 'text-red-700'}>
              {t('app:auth.password_lowercase')}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {rules.number ? (
              <Check className="w-4 h-4 text-green-600" />
            ) : (
              <XIcon className="w-4 h-4 text-red-600" />
            )}
            <span className={rules.number ? 'text-green-700' : 'text-red-700'}>
              {t('app:auth.password_number')}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {rules.special ? (
              <Check className="w-4 h-4 text-green-600" />
            ) : (
              <XIcon className="w-4 h-4 text-red-600" />
            )}
            <span className={rules.special ? 'text-green-700' : 'text-red-700'}>
              {t('app:auth.password_special')}
            </span>
          </div>
        </div>
      </div>
    );
  };

  // ===== 5. useMemo =====
  const profile = useMemo(() => pets.find(p => p.id === currentPetId) || null, [pets, currentPetId]);
  const chartData = useMemo(() => history.filter(h => h.weight).sort((a,b) => new Date(a.date).getTime() - new Date(b.date).getTime()).map(h => ({ date: formatDateFR(h.date), weight: Number(h.weight) })), [history]);
  const stoolData = useMemo(() => history.filter(h => h.stoolScore).sort((a,b) => new Date(a.date).getTime() - new Date(b.date).getTime()).map(h => ({ date: formatDateFR(h.date), score: Number(h.stoolScore) })), [history]);

  const loadPetSpecificData = async (petId: string) => {
    await reloadPetData(petId);
  };

  const petManagement = usePetManagement({
    currentUser,
    pets,
    setPets,
    setCurrentPetId,
    setIsEditProfileModalOpen: modalState.setIsEditProfileModalOpen,
    setIsPetSelectorOpen: modalState.setIsPetSelectorOpen,
    setStatusMessage,
    setIsSaving,
    subscription,
    canPerformAction,
    checkAndShowLimitBanners,
    profile,
    loadPetSpecificData,
    updateUsage,
    currentPetId
  });

  const handleSavePetProfile = petManagement.handleSavePetProfile;
  const handleDeletePet = petManagement.handleDeletePet;
  const handleSeedData = petManagement.handleSeedData;
  const handleOpenAddPet = petManagement.handleOpenAddPet;

  const loadUserData = async (userId: string) => {
    try {
      const userData = await getUserProfile(userId);
      setUserProfile(userData);

      const userPets = await storage.getPets(userId);

      if (userPets.length === 0) {
        modalState.setIsEditProfileModalOpen(true);
        petManagement.setPetForm({
          name: '',
          type: 'chien',
          breed: '',
          color: '',
          identificationNumber: '',
          birthDate: new Date().toISOString().split('T')[0],
          weight: 0,
          imageUrl: ''
        });
      } else {
        setPets(userPets);
        setCurrentPetId(userPets[0].id);
      }
    } catch (error) {
      console.error("Storage load error:", error);
    }
  };

  const handleVoiceInput = () => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      alert("Votre navigateur ne supporte pas la reconnaissance vocale.");
      return;
    }

    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.lang = 'fr-FR';
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => setIsListening(true);
    recognition.onend = () => setIsListening(false);
    recognition.onresult = async (event: any) => {
      const transcript = event.results[0][0].transcript;
      setVoiceProcessing(true);
      try {
        const data = await analyzeVoiceLog(transcript);
        setNewLog(prev => ({
          ...prev,
          weight: data.weight ? String(data.weight) : prev.weight,
          mood: data.mood ? (data.mood as any) : prev.mood,
          appetite: data.appetite ? (data.appetite as any) : prev.appetite,
          stoolScore: data.stoolScore ? data.stoolScore : prev.stoolScore,
          stoolColor: data.stoolColor ? data.stoolColor : prev.stoolColor,
          notes: data.notes ? (prev.notes ? prev.notes + '\n' + data.notes : data.notes) : prev.notes,
          time: data.time || prev.time
        }));
        setStatusMessage({ type: 'success', text: 'Analyse terminée !' });
      } catch (e) {
        setStatusMessage({ type: 'error', text: 'Erreur analyse' });
      } finally {
        setVoiceProcessing(false);
      }
    };
    recognition.start();
  };

  const handleChatVoiceInput = () => {
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) return;
    const recognition = new SpeechRecognition();
    recognition.lang = 'fr-FR';
    recognition.onstart = () => setIsChatListening(true);
    recognition.onend = () => setIsChatListening(false);
    recognition.onresult = (event: any) => setAiInput(event.results[0][0].transcript);
    recognition.start();
  };

  const handleAskAI = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!aiInput.trim() || !profile) return;

    const permission = await canPerformAction('useAiChat');
    if (!permission.allowed) {
      setReachedLimits([
        {
          type: 'ai',
          currentCount: permission.currentUsage || 0,
          maxCount: permission.limit || 0
        }
      ]);
      setStatusMessage({ type: 'error', text: permission.reason || 'Limite de conversations IA atteinte' });
      return;
    }

    const userMsg = aiInput;
    setChatMessages(prev => [...prev, { role: 'user', text: userMsg }]);
    setAiInput('');
    setAiLoading(true);
    try {
      const advice = await getHealthAdvice(profile, history, userMsg, currentLanguage);
      setChatMessages(prev => [...prev, { role: 'ai', text: advice }]);

      const newChatCount = chatMessages.length + 1;
      if (subscription) {
        await updateUsage({ currentAiChats: newChatCount });
      }
    } catch (error) {
      setChatMessages(prev => [...prev, { role: 'ai', text: "Erreur technique IA." }]);
    } finally {
      setAiLoading(false);
    }
  };

  const handleSaveReminder = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!profile || !newReminder.date) return;

    const permission = await canPerformAction('addReminder');
    if (!permission.allowed) {
      setReachedLimits([
        {
          type: 'reminder',
          currentCount: permission.currentUsage || 0,
          maxCount: permission.limit || 0
        }
      ]);
      setStatusMessage({ type: 'error', text: permission.reason || 'Limite de rappels atteinte' });
      return;
    }

    setIsSaving(true);
    try {
      const reminderEntry: Omit<Reminder, 'id'> = {
        petId: profile.id,
        type: newReminder.type,
        date: newReminder.date,
        title: newReminder.title || REMINDER_TYPES.find(r => r.value === newReminder.type)?.label || 'Rappel',
        completed: false
      };
      await saveReminderAndReload(profile.id, reminderEntry);
      modalState.setIsReminderModalOpen(false);
      setNewReminder({ type: 'veterinaire', date: '', title: '' });
      setStatusMessage({ type: 'success', text: 'Rappel programmé !' });
    } catch (error) {
      setStatusMessage({ type: 'error', text: "Échec de l'ajout." });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteReminder = async (id: string) => {
    try {
      if (!profile) return;
      await deleteReminderAndReload(profile.id, id);
      setStatusMessage({ type: 'success', text: 'Rappel supprimé' });
    } catch (e) {
      setStatusMessage({ type: 'error', text: 'Erreur suppression' });
    }
  };

  const handleSaveLog = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!profile) return;

    if (!newLog.weight && !newLog.mood && !newLog.appetite && !newLog.stoolScore && !newLog.notes) {
      setStatusMessage({ type: 'error', text: 'Remplissez au moins un champ.' });
      return;
    }

    const permission = await canPerformAction('addLog');
    if (!permission.allowed) {
      setReachedLimits([
        {
          type: 'log',
          currentCount: permission.currentUsage || 0,
          maxCount: permission.limit || 0
        }
      ]);
      setStatusMessage({ type: 'error', text: permission.reason || 'Limite de logs atteinte' });
      return;
    }

    setIsSaving(true);
    try {
      const weightNum = newLog.weight ? parseFloat(newLog.weight) : null;
      const dateTimeString = `${newLog.date}T${newLog.time}:00`;
      const fullDate = new Date(dateTimeString);

      const logEntry: Omit<HealthEntry, 'id'> = {
        petId: profile.id,
        date: fullDate.toISOString(),
        weight: weightNum,
        mood: newLog.mood || null,
        appetite: newLog.appetite || null,
        stoolScore: newLog.stoolScore ? Number(newLog.stoolScore) : null,
        stoolColor: newLog.stoolColor || null,
        symptoms: [],
        notes: newLog.notes
      };

      await saveLogAndReload(profile.id, logEntry);
      modalState.setIsLogModalOpen(false);
      setNewLog({
        date: new Date().toISOString().split('T')[0],
        time: new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }),
        weight: '',
        mood: '' as HealthEntry['mood'] | '',
        appetite: '' as HealthEntry['appetite'] | '',
        stoolScore: '' as number | '',
        stoolColor: '' as string | '',
        notes: ''
      });
      setStatusMessage({ type: 'success', text: 'Log enregistré !' });
    } catch (error) {
      setStatusMessage({ type: 'error', text: "Échec de l'ajout." });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteLog = async (id: string) => {
    try {
      if (!profile) return;
      await deleteLogAndReload(profile.id, id);
      setSelectedLog(null);
      setStatusMessage({ type: 'success', text: 'Log supprimé' });
    } catch (e) {
      setStatusMessage({ type: 'error', text: 'Erreur suppression' });
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsUploading(true);
    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = async () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const MAX = 500;
        let w = img.width, h = img.height;
        if (w > h) {
          if (w > MAX) {
            h *= MAX / w;
            w = MAX;
          }
        } else {
          if (h > MAX) {
            w *= MAX / h;
            h = MAX;
          }
        }
        canvas.width = w;
        canvas.height = h;
        ctx?.drawImage(img, 0, 0, w, h);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
        petManagement.setPetForm(prev => ({ ...prev, imageUrl: dataUrl }));
        setIsUploading(false);
      };
      img.src = event.target?.result as string;
    };
    reader.readAsDataURL(file);
  };

  // ===== 6. useEffect =====
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');
    const error = urlParams.get('error');

    if (error) {
      console.error('OAuth error:', error);
      return;
    }

    if (code && state) {
      console.log('OAuth callback detected:', { code: code.substring(0, 20) + '...', state });
      window.history.replaceState({}, document.title, window.location.pathname);
      handleCallback(code, state);
    }
  }, [handleCallback]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      try {
        console.log('Auth state changed:', { user: !!user, uid: user?.uid });
        console.log('Environment:', import.meta.env.VITE_APP_ENV);
        console.log('Firebase project:', import.meta.env.VITE_FIREBASE_PROJECT_ID);

        if (!user) {
          setCurrentUser(null);
          setPets([]);
          setCurrentPetId(null);
          setHistory([]);
          setReminders([]);
          return;
        }

        setCurrentUser(user as FirebaseUser);

        try {
          const existingSubscription = await subscriptionService.getUserSubscription(user.uid);
          if (!existingSubscription) {
            console.log('No subscription found, creating free subscription for uid:', user.uid);
            await subscriptionService.createFreeSubscription(user.uid);
            console.log('Free subscription created for uid:', user.uid);
          } else {
            console.log('Existing subscription found for uid:', user.uid, 'docId:', existingSubscription.id);
          }
        } catch (e) {
          console.error('Error ensuring subscription on login:', e);
          const err = e as any;
          const details = [err?.code, err?.message].filter(Boolean).join(' - ');
          setStatusMessage({ type: 'error', text: `Erreur création subscription${details ? ` (${details})` : ''}` });
        }

        await loadUserData(user.uid);
      } catch (err) {
        console.error('Auth error:', err);
      } finally {
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (currentPetId) {
      reloadPetData(currentPetId);
    }
  }, [currentPetId, reloadPetData]);

  useEffect(() => {
    if (statusMessage) {
      const timer = setTimeout(() => setStatusMessage(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [statusMessage]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages, aiLoading]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalState.isPetSelectorOpen && !(event.target as Element).closest('.pet-selector')) {
        modalState.setIsPetSelectorOpen(false);
      }
    };

    if (modalState.isPetSelectorOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [modalState.isPetSelectorOpen]);

  useEffect(() => {
    const handlePWAInstallable = () => {
      setShowPWAInstall(true);
    };

    window.addEventListener('pwa-installable', handlePWAInstallable);
    
    return () => {
      window.removeEventListener('pwa-installable', handlePWAInstallable);
    };
  }, []);

  useEffect(() => {
    if (subscription) {
      checkAndShowLimitBanners();
    }
  }, [subscription, checkAndShowLimitBanners]);

  useEffect(() => {
    if (subscriptionError) {
      setStatusMessage({ type: 'error', text: subscriptionError });
    }
  }, [subscriptionError]);

  useEffect(() => {
    const uid = currentUser?.uid;
    if (!uid || !subscription) return;

    const urlParams = new URLSearchParams(window.location.search);
    const stripeSuccess = urlParams.get('success') === '1';

    const storageKeyLast = `anivista:last_sub_type:${uid}`;
    const storageKeyShown = `anivista:premium_celebration_shown:${uid}`;

    const currentType = subscription.type;
    const previousType = (localStorage.getItem(storageKeyLast) as SubscriptionType | null) ?? null;

    const isNowPremium = currentType !== SubscriptionType.FREE;
    const wasFree = previousType === SubscriptionType.FREE;
    const alreadyShownForThisUpgrade = localStorage.getItem(storageKeyShown) === currentType;

    // Persist latest type
    localStorage.setItem(storageKeyLast, currentType);

    // If Stripe added success=1, clean the URL (no matter what)
    if (stripeSuccess) {
      urlParams.delete('success');
      const nextQs = urlParams.toString();
      const nextUrl = `${window.location.pathname}${nextQs ? `?${nextQs}` : ''}${window.location.hash ?? ''}`;
      window.history.replaceState({}, document.title, nextUrl);
    }

    // First run after feature rollout: persist baseline, but don't celebrate retroactively.
    if (!previousType) return;

    // Show on any upgrade to premium (including admin) OR on Stripe success once premium is effective
    if ((isNowPremium && wasFree && !alreadyShownForThisUpgrade) || (stripeSuccess && isNowPremium && !alreadyShownForThisUpgrade)) {
      localStorage.setItem(storageKeyShown, currentType);
      setIsPremiumCelebrationOpen(true);
    }
  }, [currentUser?.uid, subscription?.type]);

  const handlePWAInstall = async () => {
    const deferredPrompt = (window as any).pwaDeferredPrompt;
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const choiceResult = await deferredPrompt.userChoice;
      if (choiceResult.outcome === 'accepted') {
        console.log('Anivista: App installed');
        setShowPWAInstall(false);
      }
      (window as any).pwaDeferredPrompt = null;
    }
  };

  const handleLogout = async () => {
    await firebaseAuth.handleLogout();
  };

  const handleManageSubscription = async () => {
    try {
      const user = currentUser ?? auth.currentUser;
      if (!user) throw new Error('Utilisateur non connecté');

      const apiBase = (import.meta as any).env?.VITE_STRIPE_API_BASE_URL || 'https://api-anivista-dev.poffet.me';
      const idToken = await user.getIdToken();

      const resp = await fetch(`${apiBase}/api/stripe/create-billing-portal-session`, {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          authorization: `Bearer ${idToken}`
        }
      });

      if (!resp.ok) {
        const txt = await resp.text();
        throw new Error(`Erreur API Stripe (${resp.status}): ${txt}`);
      }

      const data = (await resp.json()) as { url?: string };
      if (!data.url) throw new Error('URL manquante');

      const newWindow = window.open(data.url, '_blank', 'noopener,noreferrer');
      if (!newWindow) {
        throw new Error("Ouverture de la page Stripe bloquée (popup) : autorisez les popups ou ouvrez depuis un navigateur.");
      }
    } catch (e) {
      const err = e as any;
      const msg = err?.message ?? 'Erreur ouverture Stripe';
      setStatusMessage({
        type: 'error',
        text:
          msg === 'Utilisateur non connecté'
            ? 'Veuillez vous connecter avec un compte (Firebase) pour gérer votre abonnement Stripe.'
            : msg
      });
    }
  };

  const handleCancelSubscription = async () => {
    try {
      const user = currentUser ?? auth.currentUser;
      if (!user) throw new Error('Utilisateur non connecté');

      setIsCancellingSubscription(true);

      const apiBase = (import.meta as any).env?.VITE_STRIPE_API_BASE_URL || 'https://api-anivista-dev.poffet.me';
      const idToken = await user.getIdToken();

      const resp = await fetch(`${apiBase}/api/stripe/cancel-subscription`, {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          authorization: `Bearer ${idToken}`
        },
        body: JSON.stringify({ mode: 'cancel_at_period_end' })
      });

      if (!resp.ok) {
        const txt = await resp.text();
        throw new Error(`Erreur API Stripe (${resp.status}): ${txt}`);
      }

      setStatusMessage({ type: 'success', text: 'Subscription résiliée' });
    } catch (e) {
      const err = e as any;
      setStatusMessage({ type: 'error', text: err?.message ?? 'Erreur résiliation' });
    } finally {
      setIsCancellingSubscription(false);
    }
  };

  const handleOpenEditUser = () => {
    const user = currentUser || oauthUser;
    if (user) {
      setUserForm({
        name: userProfile?.name || userProfile?.displayName || '',
        firstName: userProfile?.firstName || '',
        email: user.email || '',
        address: {
          street: userProfile?.address?.street || '',
          postalCode: userProfile?.address?.postalCode || '',
          city: userProfile?.address?.city || '',
          country: userProfile?.address?.country || ''
        }
      });
      setIsEditUserModalOpen(true);
    }
  };

  const handleSaveUserProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser) return;
    
    setIsSaving(true);
    try {
      await saveUserProfile(currentUser, userForm.name, userForm.firstName, userForm.address, 'user');
      
      // Recharger le profil utilisateur pour avoir les données à jour
      const updatedProfile = await getUserProfile(currentUser.uid);
      setUserProfile(updatedProfile);
      
      setIsEditUserModalOpen(false);
      setStatusMessage({ type: 'success', text: 'Profil mis à jour !' });
    } catch (error) {
      console.error("Error updating user profile:", error);
      setStatusMessage({ type: 'error', text: 'Erreur mise à jour profil' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleLogin = async (e: React.FormEvent<HTMLFormElement>) => {
    await firebaseAuth.handleLogin(e);
  };

  const handleRegister = async (e: React.FormEvent<HTMLFormElement>) => {
    await firebaseAuth.handleRegister(e);
  };

  const firebaseAuth = useFirebaseAuthActions({
    t,
    validatePassword,
    setLoading,
    setStatusMessage,
    isOAuthAuthenticated,
    oauthLogout,
    currentUser
  });

  if (loading) return <div className="flex h-screen items-center justify-center"><Loader2 className="w-10 h-10 text-indigo-600 animate-spin" /></div>;

  if (!currentUser && !isOAuthAuthenticated) {
    return (
      <div className="flex flex-col min-h-screen max-w-md mx-auto bg-white p-8 items-center justify-center text-center">
        {statusMessage && (
          <div className="fixed top-20 left-1/2 -translate-x-1/2 z-[200] w-[90%] max-w-xs animate-in slide-in-from-top">
            <div className={`flex items-center gap-3 p-4 rounded-2xl shadow-2xl border ${statusMessage.type === 'success' ? 'bg-emerald-50 border-emerald-100 text-emerald-800' : 'bg-red-50 border-red-100 text-red-800'}`}>
              {statusMessage.type === 'success' ? <CheckCircle2 size={18} /> : <AlertCircle size={18} />}
              <span className="text-sm font-bold">{statusMessage.text}</span>
            </div>
          </div>
        )}
        <div className="absolute top-4 right-4">
          <LanguageSelector />
        </div>
        <div className="w-24 h-24 bg-indigo-600 rounded-[2rem] flex items-center justify-center text-white mb-8 shadow-2xl">
          <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 32 32" fill="none">
            <rect width="32" height="32" rx="8" fill="white"/>
            <g transform="translate(4, 4)">
              <path d="M11.25 16.25h1.5L12 17z" fill="#4f46e5" stroke="none"/>
              <path d="M16 14v.5" stroke="#4f46e5" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
              <path d="M4.42 11.247A13.152 13.152 0 0 0 4 14.556C4 18.728 7.582 21 12 21s8-2.272 8-6.444a11.702 11.702 0 0 0-.493-3.309" stroke="#4f46e5" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
              <path d="M8 14v.5" stroke="#4f46e5" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
              <path d="M8.5 8.5c-.384 1.05-1.083 2.028-2.344 2.5-1.931.722-3.576-.297-3.656-1-.113-.994 1.177-6.53 4-7 1.923-.321 3.651.845 3.651 2.235A7.497 7.497 0 0 1 14 5.277c0-1.39 1.844-2.598 3.767-2.277 2.823.47 4.113 6.006 4 7-.08.703-1.725 1.722-3.656 1-1.261-.472-1.855-1.45-2.239-2.5" stroke="#4f46e5" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
            </g>
          </svg>
        </div>
        <h1 className="text-4xl font-black mb-2 tracking-tight">{t('app:auth.title')}</h1>
        <p className="text-gray-500 mb-10">{t('app:auth.subtitle')}</p>
        
        <button onClick={oauthLogin} disabled={oauthLoading} className="w-full bg-[#006237] text-white py-4 rounded-2xl font-bold shadow-xl hover:bg-[#004d2b] disabled:opacity-50 disabled:cursor-not-allowed transition-colors mb-4">
          {oauthLoading ? (
            <>
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin inline-block mr-2"></div>
              {t('app:auth.login_in_progress')}
            </>
          ) : (
            t('app:auth.login_with_drvictoria')
          )}
        </button>
        
        <div className="text-center text-gray-400 text-sm my-4">{t('common:or')}</div>
        
        {authView === 'login' && (
          <div className="w-full">
            <h2 className="text-xl font-bold mb-4 text-gray-800">{t('app:auth.login_firebase')}</h2>
            <form onSubmit={handleLogin} className="w-full space-y-4">
              <input name="email" type="email" placeholder={t('app:auth.email')} required className="w-full p-4 bg-gray-50 border rounded-2xl outline-none focus:ring-2 ring-indigo-200" />
              <input name="password" type="password" placeholder={t('app:auth.password')} required className="w-full p-4 bg-gray-50 border rounded-2xl outline-none focus:ring-2 ring-indigo-200" />
              <button type="submit" className="w-full bg-gray-600 text-white py-4 rounded-2xl font-bold">{t('app:auth.login_firebase')}</button>
            </form>
            
            <div className="mt-6 pt-6 border-t border-gray-200">
              <p className="text-gray-600 text-sm mb-3">Nouveau sur Anivista ?</p>
              <button 
                onClick={() => { setAuthView('register'); setPasswordError(null); setCurrentPassword(''); }} 
                className="w-full bg-indigo-600 text-white py-4 rounded-2xl font-bold hover:bg-indigo-700 transition-colors"
              >
                {t('app:auth.create_account')}
              </button>
            </div>
          </div>
        )}
        
        {authView === 'register' && (
          <div className="w-full">
            <h2 className="text-xl font-bold mb-4 text-gray-800">{t('app:auth.create_account')}</h2>
            <form onSubmit={handleRegister} className="w-full space-y-4" data-lpignore="true" data-form-type="other">
              <input name="email" type="email" placeholder={t('app:auth.email')} required className="w-full p-4 bg-gray-50 border rounded-2xl outline-none focus:ring-2 ring-indigo-200" data-lpignore="true" />
              <input 
                name="password" 
                type="password" 
                placeholder={t('app:auth.password')} 
                required 
                className="w-full p-4 bg-gray-50 border rounded-2xl outline-none focus:ring-2 ring-indigo-200"
                onChange={(e) => setCurrentPassword(e.target.value)}
                data-lpignore="true"
              />
              <input name="confirmPassword" type="password" placeholder={t('app:auth.confirm_password')} required className="w-full p-4 bg-gray-50 border rounded-2xl outline-none focus:ring-2 ring-indigo-200" data-lpignore="true" />
              
              {passwordError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-2xl text-sm">
                  {passwordError}
                </div>
              )}
              
              <PasswordRules password={currentPassword} />
              
              <button type="submit" className="w-full bg-gray-600 text-white py-4 rounded-2xl font-bold">{t('app:auth.register')}</button>
            </form>
            
            <div className="mt-6 pt-6 border-t border-gray-200">
              <p className="text-gray-600 text-sm mb-3">Déjà un compte ?</p>
              <button 
                onClick={() => { setAuthView('login'); setPasswordError(null); setCurrentPassword(''); }} 
                className="w-full bg-gray-200 text-gray-800 py-3 rounded-2xl font-semibold hover:bg-gray-300 transition-colors"
              >
                {t('app:auth.have_account')}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  if (!profile && !modalState.isEditProfileModalOpen) {
    if (isOAuthAuthenticated) {
      return (
        <div className="flex flex-col h-[100dvh] max-w-md mx-auto bg-white p-8 items-center justify-center text-center">
          <div className="w-24 h-24 bg-indigo-600 rounded-[2rem] flex items-center justify-center text-white mb-8 shadow-2xl">
            <Loader2 className="w-12 h-12 animate-spin" />
          </div>
          <h1 className="text-2xl font-bold mb-4">Chargement de votre profil...</h1>
          <p className="text-gray-500 mb-6">Récupération de vos animaux</p>
          <div className="bg-green-50 border border-green-200 p-4 rounded-2xl mb-6 w-full">
            <p className="text-sm text-green-800 font-medium">Statut Premium</p>
            <p className="text-xs text-green-600 mt-1">Accès complet aux fonctionnalités</p>
          </div>
          <button onClick={handleLogout} className="w-full bg-red-600 text-white py-3 rounded-2xl font-bold">
            Se déconnecter
          </button>
        </div>
      );
    }
    return <div className="flex h-screen items-center justify-center">Chargement du profil...</div>;
  }

  return (
    <div className="flex flex-col h-[100dvh] max-w-md mx-auto bg-gray-50 overflow-hidden relative shadow-2xl border-x">
      {statusMessage && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-[200] w-[90%] max-w-xs animate-in slide-in-from-top">
          <div className={`flex items-center gap-3 p-4 rounded-2xl shadow-2xl border ${statusMessage.type === 'success' ? 'bg-emerald-50 border-emerald-100 text-emerald-800' : 'bg-red-50 border-red-100 text-red-800'}`}>
            {statusMessage.type === 'success' ? <CheckCircle2 size={18} /> : <AlertCircle size={18} />}
            <span className="text-sm font-bold">{statusMessage.text}</span>
          </div>
        </div>
      )}

      <header className="bg-white p-4 border-b flex justify-between items-center z-50 shrink-0 relative">
        <button onClick={() => modalState.setIsPetSelectorOpen(!modalState.isPetSelectorOpen)} className="flex items-center gap-3 active:opacity-70 transition-opacity">
          <div className={`w-10 h-10 rounded-2xl flex items-center justify-center text-white shadow-lg overflow-hidden ${profile?.type === 'chat' ? 'bg-orange-500' : 'bg-indigo-600'}`}>
            {profile?.imageUrl ? <img src={profile.imageUrl} alt="Pet" className="w-full h-full object-cover" /> : (profile?.type === 'chat' ? <Cat size={24} /> : <Dog size={24} />)}
          </div>
          <div className="text-left">
            <h1 className="font-bold text-gray-900 leading-none flex items-center gap-1">
              {profile?.name} <ChevronDown size={14} className="text-gray-400"/>
            </h1>
            <p className="text-[10px] text-gray-400 font-black uppercase tracking-widest mt-1">{profile?.breed}</p>
          </div>
        </button>
        <button onClick={() => modalState.setIsLogModalOpen(true)} className={`${profile?.type === 'chat' ? 'bg-orange-500' : 'bg-indigo-600'} text-white p-2.5 rounded-2xl shadow-lg active:scale-90 transition-all`}>
          <Plus size={20} strokeWidth={3} />
        </button>

        {modalState.isPetSelectorOpen && (
          <div className="pet-selector absolute top-full left-4 mt-2 bg-white rounded-2xl shadow-2xl border border-gray-100 p-2 w-64 z-[100] animate-slide-up origin-top-left">
            <p className="text-[10px] font-black uppercase text-gray-400 px-3 py-2">{t('app:pet.my_pets')}</p>
            {pets.map(pet => (
              <button 
                key={pet.id} 
                onClick={() => { setCurrentPetId(pet.id); modalState.setIsPetSelectorOpen(false); }}
                className={`w-full flex items-center gap-3 p-3 rounded-xl transition-colors ${currentPetId === pet.id ? 'bg-gray-50' : 'hover:bg-gray-50'}`}
              >
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-white text-xs ${pet.type === 'chat' ? 'bg-orange-500' : 'bg-indigo-600'}`}><Activity size={16}/></div>
                <div className="text-left">
                  <p className="font-bold text-sm text-gray-900">{pet.name}</p>
                </div>
                {currentPetId === pet.id && <CheckCircle2 size={16} className="ml-auto text-green-500"/>}
              </button>
            ))}
            <div className="h-px bg-gray-100 my-2" />
            <button onClick={handleOpenAddPet} className="w-full flex items-center gap-2 p-3 text-indigo-600 font-bold text-sm hover:bg-indigo-50 rounded-xl">
              <Plus size={16} /> {t('app:pet.add_pet')}
            </button>
          </div>
        )}
      </header>

      <main className="flex-1 overflow-y-auto p-4 space-y-6 hide-scrollbar">
        {appView === 'dashboard' && profile && (
          <>
            {showPWAInstall && (
              <div className="bg-gradient-to-r from-indigo-500 to-purple-600 p-4 rounded-[2rem] shadow-sm border border-indigo-200">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h4 className="text-white font-bold text-sm mb-1">Installer Anivista</h4>
                    <p className="text-indigo-100 text-xs">Accédez à l'application hors ligne</p>
                  </div>
                  <button 
                    onClick={handlePWAInstall}
                    className="bg-white text-indigo-600 px-4 py-2 rounded-2xl font-bold text-sm active:bg-indigo-50 transition-colors"
                  >
                    Installer
                  </button>
                </div>
              </div>
            )}

            {/* Bannière consolidée des limites atteintes */}
            {reachedLimits.length > 0 && (
              <LimitReachedBanner
                reachedLimits={reachedLimits}
                onUpgrade={() => setIsUpgradeModalOpen(true)}
                onDismiss={() => {}}
              />
            )}

            <WeightChart
              profile={profile}
              chartData={chartData}
              stoolData={stoolData}
            />

            <PetStatsCards
              profile={profile}
              history={history}
            />

            <AIAdvisor
              profile={profile}
              onOpenAI={() => setAppView('ai')}
            />
            
            <RemindersList
              reminders={reminders}
              onAddReminder={() => modalState.setIsReminderModalOpen(true)}
              onDeleteReminder={handleDeleteReminder}
              formatDateFR={formatDateFR}
            />
          </>
        )}

        {appView === 'history' && (
          <HistoryView
            history={history}
            setSelectedLog={setSelectedLog}
            formatDateFR={formatDateFR}
            formatTimeFR={formatTimeFR}
          />
        )}

        {appView === 'ai' && (
          <AIChatView
            profile={profile}
            chatMessages={chatMessages}
            aiLoading={aiLoading}
            aiInput={aiInput}
            setAiInput={setAiInput}
            handleAskAI={handleAskAI}
            handleChatVoiceInput={handleChatVoiceInput}
          />
        )}

        {appView === 'profile' && (
          <ProfileView
            currentUser={currentUser}
            oauthUser={oauthUser}
            userForm={userForm}
            userProfile={userProfile}
            isFree={isFree}
            subscription={subscription}
            onUpgrade={() => setIsUpgradeModalOpen(true)}
            onManageSubscription={handleManageSubscription}
            onCancelSubscription={handleCancelSubscription}
            isCancellingSubscription={isCancellingSubscription}
            pets={pets}
            currentPetId={currentPetId}
            setCurrentPetId={setCurrentPetId}
            showPWAInstall={showPWAInstall}
            handlePWAInstall={handlePWAInstall}
            handleOpenEditUser={handleOpenEditUser}
            handleLogout={handleLogout}
            handleOpenEditPet={petManagement.handleOpenEditPet}
            handleDeletePet={handleDeletePet}
            handleSeedData={handleSeedData}
            handleOpenAddPet={handleOpenAddPet}
            formatDateFR={formatDateFR}
            isSaving={isSaving}
            profile={profile}
          />
        )}
      </main>

      <nav className="shrink-0 w-full bg-white/95 backdrop-blur-md border-t p-4 flex justify-around items-center pb-8 z-10">
        <button onClick={() => setAppView('dashboard')} className={`p-3 rounded-2xl transition-all relative ${appView === 'dashboard' ? 'text-indigo-600 bg-gray-100' : 'text-gray-300 hover:text-gray-400'}`}>
          <LayoutDashboard size={22}/>
          {appView === 'dashboard' && <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-indigo-600" />}
        </button>
        <button onClick={() => setAppView('history')} className={`p-3 rounded-2xl transition-all relative ${appView === 'history' ? 'text-indigo-600 bg-gray-100' : 'text-gray-300 hover:text-gray-400'}`}>
          <HistoryIcon size={22}/>
          {appView === 'history' && <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-indigo-600" />}
        </button>
        <button onClick={() => setAppView('ai')} className={`p-3 rounded-2xl transition-all relative ${appView === 'ai' ? 'text-indigo-600 bg-gray-100' : 'text-gray-300 hover:text-gray-400'}`}>
          <BrainCircuit size={22}/>
          {appView === 'ai' && <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-indigo-600" />}
        </button>
        <button onClick={() => setAppView('profile')} className={`p-3 rounded-2xl transition-all relative ${appView === 'profile' ? 'text-indigo-600 bg-gray-100' : 'text-gray-300 hover:text-gray-400'}`}>
          <UserCircle size={22}/>
          {appView === 'profile' && <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-indigo-600" />}
        </button>
      </nav>

      <PetEditModal
        isOpen={modalState.isEditProfileModalOpen}
        onClose={() => modalState.setIsEditProfileModalOpen(false)}
        petForm={petManagement.petForm}
        setPetForm={petManagement.setPetForm}
        onSubmit={handleSavePetProfile}
        isSaving={isSaving}
        petsLength={pets.length}
        currentPetId={currentPetId}
        onLogout={handleLogout}
      />

      <ReminderModal
        isOpen={modalState.isReminderModalOpen}
        onClose={() => modalState.setIsReminderModalOpen(false)}
        onSubmit={handleSaveReminder}
        isSaving={isSaving}
        newReminder={newReminder}
        setNewReminder={setNewReminder}
        petType={profile?.type || 'chien'}
      />

      {modalState.isLogModalOpen && profile && (
        <LogForm
          profile={profile}
          newLog={newLog}
          setNewLog={setNewLog}
          isListening={isListening}
          handleVoiceInput={handleVoiceInput}
          handleImageUpload={handleImageUpload}
          isUploading={isUploading}
          onClose={() => modalState.setIsLogModalOpen(false)}
          onSave={handleSaveLog}
          isSaving={isSaving}
          voiceProcessing={voiceProcessing}
        />
      )}

      <UserEditModal
        isEditUserModalOpen={isEditUserModalOpen}
        setIsEditUserModalOpen={setIsEditUserModalOpen}
        userForm={userForm}
        setUserForm={setUserForm}
        isSaving={isSaving}
        handleSaveUserProfile={handleSaveUserProfile}
      />

      <UpgradeModal
        isOpen={isUpgradeModalOpen}
        onClose={() => setIsUpgradeModalOpen(false)}
        currentPlan={subscription?.type}
      />

      <PremiumCelebrationModal
        isOpen={isPremiumCelebrationOpen}
        onClose={() => setIsPremiumCelebrationOpen(false)}
      />
        
      <LogDetailModal
        selectedLog={selectedLog}
        onClose={() => setSelectedLog(null)}
        onDelete={handleDeleteLog}
        formatDateFR={formatDateFR}
        formatTimeFR={formatTimeFR}
      />
    </div>
  );
};
export default App;